import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { CatalogMetadata } from '../../models/catalog-metadata.model';
import { CatalogScope } from '../../models/catalog-scope.enum';
import { TemperatureDerationCatalog } from '../../models/temperature-deration-catalog.model';
import { UnitsGridColumn } from 'src/app/shared/components/units-grid-input/units-grid-input.models';

export interface TemperatureDerationPoint {
  temperature: number;
  derationFactor: number;
}

export const DEFAULT_TEMP_DERATION_ROW: TemperatureDerationPoint = {
  temperature: 0,
  derationFactor: 1
};

export function createTempDerationColumns(): readonly UnitsGridColumn[] {
  return [
    {
      kind: 'number',
      field: 'temperature',
      header: 'Temperature',
      unitKey: 'temperature',
      minFractionDigits: 2,
      maxFractionDigits: 6,
      formatFractionDigits: 4
    },
    {
      kind: 'number',
      field: 'derationFactor',
      header: 'Deration Factor',
      minFractionDigits: 2,
      maxFractionDigits: 6,
      formatFractionDigits: 4
    }
  ];
}

export function createTempDerationRow(data?: Partial<TemperatureDerationPoint>): FormGroup {
  return new FormGroup({
    temperature: new FormControl<number | null>(data?.temperature ?? null, [
      Validators.required
    ]),
    derationFactor: new FormControl<number | null>(data?.derationFactor ?? null, [
      Validators.required
    ])
  });
}

export function isTempDerationRowEmpty(row: FormGroup): boolean {
  const temp = row.get('temperature')?.value;
  const factor = row.get('derationFactor')?.value;

  const isTempEmpty = temp === null || temp === undefined || temp === '' || Number.isNaN(Number(temp));
  const isFactorEmpty = factor === null || factor === undefined || factor === '' || Number.isNaN(Number(factor));

  return isTempEmpty && isFactorEmpty;
}

export function ensureTrailingInputRow(formArray: FormArray<FormGroup>): void {
  if (formArray.length === 0) {
    formArray.push(createTempDerationRow());
    return;
  }

  while (
    formArray.length > 1 &&
    isTempDerationRowEmpty(formArray.at(formArray.length - 1)) &&
    isTempDerationRowEmpty(formArray.at(formArray.length - 2))
  ) {
    formArray.removeAt(formArray.length - 1);
  }

  const lastRow = formArray.at(formArray.length - 1);
  if (!isTempDerationRowEmpty(lastRow)) {
    formArray.push(createTempDerationRow());
  }
}

export function populateTempDerationFormArray(
  formArray: FormArray<FormGroup>,
  profileData?: readonly TemperatureDerationPoint[]
): void {
  formArray.clear();
  const rows = profileData && profileData.length > 0 ? profileData : [];

  rows.forEach((item) => {
    formArray.push(createTempDerationRow(item));
  });

  ensureTrailingInputRow(formArray);
}

export function extractTempDerationRows(formArray: FormArray<FormGroup>): TemperatureDerationPoint[] {
  const validRows: TemperatureDerationPoint[] = formArray.controls
    .filter((control) => !isTempDerationRowEmpty(control as FormGroup))
    .map((control) => ({
      temperature: Number(control.get('temperature')?.value ?? 0),
      derationFactor: Number(control.get('derationFactor')?.value ?? 1)
    }));

  const uniqueRows = validRows.filter(
    (row, index, self) =>
      index === self.findIndex((r) => r.temperature === row.temperature && r.derationFactor === row.derationFactor)
  );

  return uniqueRows.length === 0 ? [DEFAULT_TEMP_DERATION_ROW] : uniqueRows;
}

export function createNewTemperatureDerationProfile(defaultTemperature: number, emptyGuid: string): TemperatureDerationCatalog {
  return new TemperatureDerationCatalog({
    id: emptyGuid,
    name: '',
    temperatureDerationProfile: [
      {
        temperature: defaultTemperature,
        derationFactor: 1
      }
    ],
    isLocked: false,
    metadata: new CatalogMetadata(),
    scope: CatalogScope.User
  });
}
